/**
 * Auto-refresh hook for work items with smart caching.
 *
 * Features:
 * - Configurable auto-refresh interval (default: 60 seconds)
 * - On auto-refresh: fetches details for NEW items AND items with STATUS CHANGES
 *   (status change indicates EHR update, so we refresh unsigned_sections)
 * - On tab change: shows cached data immediately, refreshes all items in background
 * - Per-tab cache (MDS vs Care) - persists across component mount/unmount
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { WorkItem, AssessmentQueryParams } from "../types/workItems";
import { FilterState } from "../types/dashboard";
import { apiService, type ApiService } from "../services/apiService";
import { useRequestVersion } from "../utils/requestVersion";
import { convertTimeRangeToDateRange } from "../utils/dashboardUtils";

// Default constants
const DEFAULT_REFRESH_INTERVAL_MS = 60 * 1000; // 60 seconds
const CONCURRENCY_LIMIT = 5;

export type TabType = "mds" | "care";

// Module-level cache that persists across component mount/unmount
// Maps work item ID to enriched work item data (with unsigned_sections, etc.)
const workItemCache: Record<TabType, Map<string, WorkItem>> = {
  mds: new Map(),
  care: new Map(),
};

// Track last known item IDs per tab (for detecting new items)
const lastKnownIds: Record<TabType, Set<string>> = {
  mds: new Set(),
  care: new Set(),
};

interface UseWorkItemsAutoRefreshOptions {
  filters: FilterState;
  tab: TabType;
  /** Set to true when switching TO this tab (triggers full refresh) */
  isTabChange?: boolean;
  enabled?: boolean;
  /** Custom API service instance (for widget usage with different auth) */
  api?: ApiService;
  /** Query key prefix to avoid conflicts between app and widget caches */
  queryKeyPrefix?: string;
  /** Auto-refresh interval in milliseconds (default: 60000 = 60 seconds). Set to 0 to disable auto-refresh. */
  refreshIntervalMs?: number;
  /** Explicit facility ID to filter by (used in multi-facility mode when a specific facility is selected) */
  facilityId?: string;
}

interface UseWorkItemsAutoRefreshResult {
  /** Work items with enriched data (includes cached unsigned_sections, etc.) */
  workItems: WorkItem[];
  /** True during initial load */
  isLoading: boolean;
  /** True during background refresh of individual items */
  isRefreshing: boolean;
  /** Error object if fetch failed */
  error: Error | null;
  /** Manually trigger a refresh (refreshes all items) */
  refresh: () => void;
  /** Last refresh timestamp */
  lastRefreshedAt: Date | null;
  /** Count of items in cache */
  cacheSize: number;
}

/**
 * Convert frontend FilterState to backend AssessmentQueryParams
 * @internal Exported for testing purposes
 */
export function mapFiltersToParams(filters: FilterState): AssessmentQueryParams {
  const params: AssessmentQueryParams = {
    limit: 100,
    sort_by: "due_date",
    sort_order: "asc",
  };

  // Status mapping
  if (filters.quickStatusFilters && filters.quickStatusFilters.length > 0) {
    params.status = filters.quickStatusFilters.join(",");
  }

  // Payer mapping (NETSOL-12004: multi-select — comma-separated)
  if (filters.payer && filters.payer.length > 0) {
    params.payer = filters.payer.join(",");
  }

  // Assessment type mapping (NETSOL-12004: multi-select — comma-separated)
  if (filters.assessmentType && filters.assessmentType.length > 0) {
    params.assessment_type = filters.assessmentType.join(",");
  }

  // Station mapping
  if (filters.station && filters.station.length > 0) {
    params.station = filters.station.join(",");
  }

  // Location mapping
  if (filters.location && filters.location.trim() !== "") {
    params.location = filters.location.trim();
  }

  // Assigned owner mapping
  if (filters.assignedOwner && filters.assignedOwner !== "all") {
    params.assigned_to = filters.assignedOwner;
  }

  // Days open mapping
  if (filters.daysOpen && filters.daysOpen.trim() !== "") {
    const daysOpenNum = parseInt(filters.daysOpen.trim(), 10);
    if (!isNaN(daysOpenNum) && daysOpenNum >= 0) {
      params.days_open = daysOpenNum;
    }
  }

  // Due date mapping. The dueDate filter value is polymorphic:
  //   - "no_due_date"                -> passed straight through
  //   - "YYYY-MM-DD,YYYY-MM-DD"      -> already in backend range format
  //     (custom range, open-ended ",end" / "start,", and the on-load default
  //      from getDefaultDueDateFilter); passed straight through
  //   - a preset label ("Today", "Next 7 Days", ...) -> resolved to a range
  // NOTE: comma-format values MUST be passed through. They are not valid
  // labels, so routing them through convertTimeRangeToDateRange returns null
  // and silently drops the filter (NETSOL-11598).
  if (filters.dueDate && filters.dueDate.trim() !== "") {
    const dueDateValue = filters.dueDate.trim();
    if (dueDateValue === "no_due_date") {
      params.due_date = "no_due_date";
    } else if (dueDateValue.includes(",")) {
      // Already a "startDate,endDate" range (custom or default) - send as-is.
      params.due_date = dueDateValue;
    } else {
      const dateRange = convertTimeRangeToDateRange(dueDateValue);
      if (dateRange) {
        // Send preset date range as "startDate,endDate" format
        params.due_date = `${dateRange.startDate},${dateRange.endDate}`;
      }
    }
  }

  // Global search / resident name search mapping to search_query
  if (filters.globalSearch && filters.globalSearch.trim() !== "") {
    params.search_query = filters.globalSearch.trim();
  } else if (filters.residentName && filters.residentName.trim() !== "") {
    params.search_query = filters.residentName.trim();
  }

  return params;
}

/**
 * Fetch individual work item details with error handling
 */
async function fetchWorkItemDetails(
  workItemId: string,
  api: ApiService,
  signal?: AbortSignal
): Promise<WorkItem | null> {
  try {
    return await api.getSingleWorkItem(workItemId, signal);
  } catch (err) {
    // Ignore AbortError - it's expected when requests are cancelled
    if (err instanceof DOMException && err.name === 'AbortError') {
      return null;
    }
    console.error(`[AutoRefresh] Failed to fetch work item ${workItemId}:`, err);
    return null;
  }
}

/**
 * Fetch multiple work items in parallel with concurrency limit
 */
async function fetchWorkItemsBatch(
  items: WorkItem[],
  api: ApiService,
  signal: AbortSignal,
  onProgress?: (completed: number, total: number) => void
): Promise<Map<string, WorkItem>> {
  const results = new Map<string, WorkItem>();
  let completed = 0;

  for (let i = 0; i < items.length; i += CONCURRENCY_LIMIT) {
    // Check if aborted before starting next batch
    if (signal.aborted) {
      throw new DOMException('Batch fetch aborted', 'AbortError');
    }

    const batch = items.slice(i, i + CONCURRENCY_LIMIT);
    const batchResults = await Promise.all(
      batch.map((item) => fetchWorkItemDetails(item.id, api, signal))
    );

    batchResults.forEach((fetchedItem, index) => {
      if (fetchedItem) {
        results.set(batch[index].id, fetchedItem);
      }
      completed++;
    });

    onProgress?.(completed, items.length);
  }

  return results;
}

/**
 * Hook for auto-refreshing work items with smart caching.
 *
 * Behavior:
 * - On auto-refresh: fetches list, calls GET /work-items/{id} for NEW items AND items with STATUS CHANGES
 *   (status change triggers refresh to get updated unsigned_sections from EHR)
 * - On tab change (isTabChange=true): shows cached data immediately, refreshes ALL items in background
 *
 * @example
 * ```tsx
 * const { workItems, isLoading, isRefreshing, refresh } = useWorkItemsAutoRefresh({
 *   filters,
 *   tab: "mds",
 *   isTabChange: wasTabChange,
 *   refreshIntervalMs: 30000, // 30 seconds (optional, defaults to 60s)
 * });
 * ```
 */
export function useWorkItemsAutoRefresh({
  filters,
  tab,
  isTabChange = false,
  enabled = true,
  api = apiService,
  queryKeyPrefix = "work-items-auto-refresh",
  refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS,
  facilityId,
}: UseWorkItemsAutoRefreshOptions): UseWorkItemsAutoRefreshResult {
  // Request versioning to detect stale responses
  const versionTracker = useRequestVersion();

  // AbortController for batch fetches
  const batchAbortControllerRef = useRef<AbortController | null>(null);

  // Background refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [forceRefreshAll, setForceRefreshAll] = useState(isTabChange);

  // Enriched work items (merged with cache)
  const [enrichedWorkItems, setEnrichedWorkItems] = useState<WorkItem[]>(() => {
    // Initialize with cached data if available
    const cache = workItemCache[tab];
    const knownIds = lastKnownIds[tab];
    if (knownIds.size > 0) {
      return Array.from(knownIds)
        .map((id) => cache.get(id))
        .filter((item): item is WorkItem => item !== undefined);
    }
    return [];
  });

  // Get the appropriate API call based on tab
  const fetchWorkItems = useCallback(
    async ({ signal }: { signal?: AbortSignal }) => {
      const params = mapFiltersToParams(filters);
      // In multi-facility mode, pass selected facility to filter results
      if (facilityId) {
        params.facility_id = facilityId;
      }
      if (tab === "mds") {
        const response = await api.getMdsAssessments(params, signal);
        return response.mds_assessments;
      } else {
        const response = await api.getCareAssessments(params, signal);
        return response.care_assessments;
      }
    },
    [filters, tab, api, facilityId]
  );

  // Main query for fetching the list
  const {
    data: listData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [queryKeyPrefix, tab, filters, facilityId],
    queryFn: fetchWorkItems,
    staleTime: refreshIntervalMs,
    // Only auto-refresh if interval > 0
    refetchInterval: refreshIntervalMs > 0 ? refreshIntervalMs : false,
    enabled,
  });

  // Track previous filter values to detect actual changes (not just reference changes)
  const prevFiltersRef = useRef<string>("");

  // Clear cache when filters or tab change to prevent stale data
  useEffect(() => {
    const currentFiltersJson = JSON.stringify(filters);

    // Only clear cache if filter VALUES actually changed (not just object reference)
    if (prevFiltersRef.current === currentFiltersJson) {
      return; // No actual change, skip clearing
    }
    prevFiltersRef.current = currentFiltersJson;

    const cache = workItemCache[tab];
    const knownIds = lastKnownIds[tab];

    // Clear cache on filter change
    cache.clear();
    knownIds.clear();
    setEnrichedWorkItems([]);

    // Abort any in-flight batch fetches
    if (batchAbortControllerRef.current) {
      batchAbortControllerRef.current.abort();
      batchAbortControllerRef.current = null;
    }

    // Reset version tracker
    versionTracker.reset();
  }, [filters, tab, versionTracker]);

  // Handle tab change flag
  useEffect(() => {
    if (isTabChange) {
      setForceRefreshAll(true);
    }
  }, [isTabChange]);

  // Process work items when list data changes
  useEffect(() => {
    if (!listData) return;

    // Get current version for this fetch to detect stale responses
    const currentVersion = versionTracker.next();

    const workItems = listData;
    const cache = workItemCache[tab];
    const knownIds = lastKnownIds[tab];
    const currentIds = new Set(workItems.map((item) => item.id));

    // Determine which items need to be fetched
    let itemsToFetch: WorkItem[] = [];

    if (forceRefreshAll) {
      // Tab change or manual refresh: refresh ALL items
      itemsToFetch = [...workItems];
    } else {
      // Auto-refresh: fetch NEW items AND items with STATUS or ARD CHANGES
      itemsToFetch = workItems.filter((item) => {
        // New item - not seen before
        if (!knownIds.has(item.id)) {
          return true;
        }
        // Existing item - check if status changed (indicates EHR update)
        const cached = cache.get(item.id);
        if (cached) {
          if (cached.status !== item.status) return true;
          // Re-fetch if ARD changed (MDS combine scenario)
          if (cached.assessment_reference_day !== item.assessment_reference_day) return true;
        }
        return false;
      });
    }

    // Merge list data with cache for immediate display
    const mergedItems: WorkItem[] = workItems.map((item) => {
      const cached = cache.get(item.id);
      if (cached && !forceRefreshAll) {
        // Merge: use new list data but preserve enriched fields from cache
        return {
          ...item,
          // Preserve unsigned_sections from cache (comes from individual GET calls)
          ...(cached.unsigned_sections !== undefined && {
            unsigned_sections: cached.unsigned_sections,
          }),
          // Preserve other enriched fields as needed
        } as WorkItem;
      }
      return item;
    });

    // Update state immediately with merged data (stale-while-revalidate)
    setEnrichedWorkItems(mergedItems);

    // Fetch details for items that need it (in background)
    if (itemsToFetch.length > 0) {
      setIsRefreshing(true);

      // Create new AbortController for this batch
      if (batchAbortControllerRef.current) {
        batchAbortControllerRef.current.abort();
      }
      batchAbortControllerRef.current = new AbortController();
      const batchSignal = batchAbortControllerRef.current.signal;

      fetchWorkItemsBatch(itemsToFetch, api, batchSignal)
        .then((fetchedItems) => {
          // Check if this response is still relevant
          if (!versionTracker.isCurrent(currentVersion)) {
            return;
          }

          // Check if aborted
          if (batchSignal.aborted) {
            return;
          }

          // Update cache with fetched data
          fetchedItems.forEach((item, id) => {
            cache.set(id, item);
            knownIds.add(id);
          });

          // Update enriched items with fetched data
          const updatedItems = workItems.map((item) => {
            const fetched = fetchedItems.get(item.id);
            if (fetched) {
              return fetched;
            }
            // Use cached or original
            return cache.get(item.id) || item;
          });

          setEnrichedWorkItems(updatedItems);
          setLastRefreshedAt(new Date());
        })
        .catch((err) => {
          // Ignore AbortError - it's expected when requests are cancelled
          if (err instanceof DOMException && err.name === 'AbortError') {
            return;
          }
          console.error("[AutoRefresh] Error fetching item details:", err);
        })
        .finally(() => {
          setIsRefreshing(false);
          setForceRefreshAll(false);
        });
    } else {
      // No items to fetch, just update the known IDs
      currentIds.forEach((id) => knownIds.add(id));
      setLastRefreshedAt(new Date());
      setForceRefreshAll(false);
    }

    // Clean up cache: remove items that are no longer in the list
    for (const cachedId of Array.from(knownIds)) {
      if (!currentIds.has(cachedId)) {
        cache.delete(cachedId);
        knownIds.delete(cachedId);
      }
    }

    // Cleanup function: abort batch fetch if dependencies change or component unmounts
    return () => {
      if (batchAbortControllerRef.current) {
        batchAbortControllerRef.current.abort();
      }
    };
  }, [listData, tab, forceRefreshAll, api, versionTracker]);

  // Manual refresh function
  const refresh = useCallback(() => {
    setForceRefreshAll(true);
    refetch();
  }, [refetch]);

  // Cache size for debugging
  const cacheSize = useMemo(() => workItemCache[tab].size, [tab, enrichedWorkItems]);

  return {
    workItems: enrichedWorkItems,
    isLoading,
    isRefreshing,
    error: error as Error | null,
    refresh,
    lastRefreshedAt,
    cacheSize,
  };
}

/**
 * Clear the cache for a specific tab or all tabs
 */
export function clearWorkItemCache(tab?: TabType): void {
  if (tab) {
    workItemCache[tab].clear();
    lastKnownIds[tab].clear();
  } else {
    workItemCache.mds.clear();
    workItemCache.care.clear();
    lastKnownIds.mds.clear();
    lastKnownIds.care.clear();
  }
}

/**
 * Query keys for use with React Query's manual cache invalidation
 */
export const workItemsAutoRefreshKeys = {
  all: ["work-items-auto-refresh"] as const,
  tab: (tab: TabType) => [...workItemsAutoRefreshKeys.all, tab] as const,
  list: (tab: TabType, filters: FilterState) =>
    [...workItemsAutoRefreshKeys.tab(tab), filters] as const,
};
